<script lang="ts">
import type { ModalEmits, ModalProps, ModalSlots } from '#ui/types';
</script>

<script setup lang="ts">
import Modal from '@nuxt/ui/components/Modal.vue';
import { reactiveOmit } from '@vueuse/core';
import { useForwardExpose, useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';
import { preventExtensionDismiss } from '#layers/ui/utils/extension-dismiss';

const props = defineProps<ModalProps>();
const emit = defineEmits<ModalEmits>();
const slots = defineSlots<ModalSlots>();

const forward = useForwardPropsEmits(reactiveOmit(props, 'content'), emit);
const { forwardRef } = useForwardExpose();

const guardedContent = computed<ModalProps['content']>(() => {
	const provided = props.content ?? {};

	return {
		...provided,
		onPointerDownOutside: withGuard(provided.onPointerDownOutside),
		onFocusOutside: withGuard(provided.onFocusOutside),
		onInteractOutside: withGuard(provided.onInteractOutside),
	};
});

function withGuard<E extends Event>(handler?: (event: E) => void) {
	return (event: E) => {
		preventExtensionDismiss(event);
		handler?.(event);
	};
}
</script>

<template>
	<Modal
		:ref="forwardRef"
		v-bind="forward"
		:content="guardedContent"
	>
		<template v-for="(_, name) in slots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>
	</Modal>
</template>
